import * as React from 'react'
import { CircleIcon } from 'lucide-react'
import { CheckIcon, ChevronRightIcon } from '@/src/icons'
import { Menubar as MenubarPrimitive } from '@base-ui/react/menubar'
import { Menu as MenuPrimitive } from '@base-ui/react/menu'

import { cn } from '@/registry/reapit/lib/utils'

// ---------------------------------------------------------------------------
// Menubar — migrated from Radix to Base UI
// ---------------------------------------------------------------------------
// Structural changes:
//   Radix: Menubar.Root > Menubar.Menu > Menubar.Trigger + Menubar.Content
//   Base UI: Menubar (container) > Menu.Root (each menu) > Menu.Trigger + Portal > Positioner > Popup
//
// The Base UI Menubar is a simple container that wraps multiple Menu.Root
// instances, providing keyboard navigation between them (arrow left/right).
//
// Sub-component renames:
//   Menubar.Menu    → Menu.Root (inside Menubar container)
//   Menubar.Content → Portal > Positioner > Popup (baked into MenubarContent)
//   Menubar.Label   → Menu.GroupLabel
//   Menubar.Sub     → Menu.SubmenuRoot
//   Menubar.SubTrigger  → Menu.SubmenuTrigger
//   Menubar.SubContent  → Portal > Positioner > Popup (baked into MenubarSubContent)
//   Menubar.ItemIndicator → Menu.CheckboxItemIndicator / Menu.RadioItemIndicator
//
// CSS variable renames:
//   --radix-menubar-content-transform-origin → --transform-origin
//
// Data attribute changes:
//   data-[state=open/closed] → data-[open] / data-[starting-style] / data-[ending-style]
//   data-[state=open] on triggers → data-[popup-open]
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Menubar (Root container)
// ---------------------------------------------------------------------------

function Menubar({ className, ...props }: React.ComponentProps<typeof MenubarPrimitive>) {
  return (
    <MenubarPrimitive
      data-slot="menubar"
      className={cn('flex h-9 items-center gap-1 rounded-md border bg-background p-1 shadow-xs', className)}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// MenubarMenu — wraps a Menu.Root inside the menubar
// ---------------------------------------------------------------------------

function MenubarMenu({
  ...props
}: Omit<React.ComponentProps<typeof MenuPrimitive.Root>, 'children'> & {
  children?: React.ReactNode
}) {
  return <MenuPrimitive.Root data-slot="menubar-menu" {...props} />
}

// ---------------------------------------------------------------------------
// MenubarGroup
// ---------------------------------------------------------------------------

function MenubarGroup({ ...props }: React.ComponentProps<typeof MenuPrimitive.Group>) {
  return <MenuPrimitive.Group data-slot="menubar-group" {...props} />
}

// ---------------------------------------------------------------------------
// MenubarPortal
// ---------------------------------------------------------------------------

function MenubarPortal({ ...props }: React.ComponentProps<typeof MenuPrimitive.Portal>) {
  return <MenuPrimitive.Portal data-slot="menubar-portal" {...props} />
}

// ---------------------------------------------------------------------------
// MenubarRadioGroup
// ---------------------------------------------------------------------------

function MenubarRadioGroup({
  onValueChange,
  ...props
}: Omit<React.ComponentProps<typeof MenuPrimitive.RadioGroup>, 'onValueChange'> & {
  onValueChange?: (value: string) => void
}) {
  const handleValueChange = React.useCallback(
    (value: unknown) => {
      onValueChange?.(String(value ?? ''))
    },
    [onValueChange],
  )

  return (
    <MenuPrimitive.RadioGroup
      data-slot="menubar-radio-group"
      onValueChange={onValueChange ? handleValueChange : undefined}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// MenubarTrigger
// ---------------------------------------------------------------------------

function MenubarTrigger({
  className,
  ...props
}: Omit<React.ComponentProps<typeof MenuPrimitive.Trigger>, 'render'> & {
  children?: React.ReactNode
}) {
  return (
    <MenuPrimitive.Trigger
      data-slot="menubar-trigger"
      className={cn(
        'flex items-center rounded-sm px-2 py-1 text-sm font-medium outline-hidden select-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[popup-open]:bg-accent data-[popup-open]:text-accent-foreground',
        className,
      )}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// MenubarContent
// ---------------------------------------------------------------------------
// Bakes in Portal > Positioner > Popup so consumers keep the same simple API.

function MenubarContent({
  className,
  align = 'start',
  alignOffset = -4,
  sideOffset = 8,
  side = 'bottom',
  children,
  ...props
}: {
  className?: string
  align?: 'start' | 'center' | 'end'
  alignOffset?: number
  sideOffset?: number
  side?: 'top' | 'right' | 'bottom' | 'left'
  children?: React.ReactNode
} & Omit<React.ComponentProps<typeof MenuPrimitive.Popup>, 'className' | 'children'>) {
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Positioner side={side} sideOffset={sideOffset} align={align} alignOffset={alignOffset}>
        <MenuPrimitive.Popup
          data-slot="menubar-content"
          className={cn(
            // DS: radius-lg (8 px) to match other floating menu surfaces.
            // Base UI: --transform-origin replaces --radix-menubar-content-transform-origin
            'z-50 min-w-[12rem] origin-[var(--transform-origin)] overflow-hidden rounded-lg border bg-popover p-1 text-popover-foreground shadow-md outline-hidden transition-[opacity,scale] duration-150 scale-100 opacity-100 data-[starting-style]:scale-95 data-[starting-style]:opacity-0 data-[ending-style]:scale-95 data-[ending-style]:opacity-0',
            className,
          )}
          {...props}
        >
          {children}
        </MenuPrimitive.Popup>
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  )
}

// ---------------------------------------------------------------------------
// MenubarItem
// ---------------------------------------------------------------------------

function MenubarItem({
  className,
  inset,
  variant = 'default',
  onSelect,
  ...props
}: Omit<React.ComponentProps<typeof MenuPrimitive.Item>, 'render'> & {
  inset?: boolean
  variant?: 'default' | 'destructive'
  /** Radix compat — called when the item is selected */
  onSelect?: () => void
  children?: React.ReactNode
}) {
  return (
    // DS: py-2 (8 px) item height, icons at size-4 (icon-sm = 16 px).
    <MenuPrimitive.Item
      data-slot="menubar-item"
      data-inset={inset}
      data-variant={variant}
      onClick={onSelect}
      className={cn(
        "relative flex cursor-default items-center gap-2 rounded-sm px-2 py-2 text-sm outline-hidden select-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 data-[variant=destructive]:text-destructive data-[variant=destructive]:data-[highlighted]:bg-destructive/10 data-[variant=destructive]:data-[highlighted]:text-destructive dark:data-[variant=destructive]:data-[highlighted]:bg-destructive/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground data-[variant=destructive]:*:[svg]:text-destructive!",
        className,
      )}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// MenubarCheckboxItem
// ---------------------------------------------------------------------------

function MenubarCheckboxItem({
  className,
  children,
  checked,
  onCheckedChange,
  ...props
}: Omit<React.ComponentProps<typeof MenuPrimitive.CheckboxItem>, 'render' | 'onCheckedChange'> & {
  children?: React.ReactNode
  onCheckedChange?: (checked: boolean) => void
}) {
  const handleCheckedChange = React.useCallback(
    (newChecked: boolean) => {
      onCheckedChange?.(newChecked)
    },
    [onCheckedChange],
  )

  return (
    <MenuPrimitive.CheckboxItem
      data-slot="menubar-checkbox-item"
      className={cn(
        "relative flex cursor-default items-center gap-2 rounded-sm py-2 pr-2 pl-8 text-sm outline-hidden select-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      checked={checked}
      onCheckedChange={onCheckedChange ? handleCheckedChange : undefined}
      {...props}
    >
      <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
        <MenuPrimitive.CheckboxItemIndicator keepMounted={false}>
          <CheckIcon className="size-4" />
        </MenuPrimitive.CheckboxItemIndicator>
      </span>
      {children}
    </MenuPrimitive.CheckboxItem>
  )
}

// ---------------------------------------------------------------------------
// MenubarRadioItem
// ---------------------------------------------------------------------------

function MenubarRadioItem({
  className,
  children,
  ...props
}: Omit<React.ComponentProps<typeof MenuPrimitive.RadioItem>, 'render'> & {
  children?: React.ReactNode
}) {
  return (
    <MenuPrimitive.RadioItem
      data-slot="menubar-radio-item"
      className={cn(
        "relative flex cursor-default items-center gap-2 rounded-sm py-2 pr-2 pl-8 text-sm outline-hidden select-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
        <MenuPrimitive.RadioItemIndicator keepMounted={false}>
          <CircleIcon className="size-2 fill-current" />
        </MenuPrimitive.RadioItemIndicator>
      </span>
      {children}
    </MenuPrimitive.RadioItem>
  )
}

// ---------------------------------------------------------------------------
// MenubarLabel
// ---------------------------------------------------------------------------

function MenubarLabel({
  className,
  inset,
  ...props
}: React.ComponentProps<'div'> & {
  inset?: boolean
}) {
  // Base UI's GroupLabel requires a parent Menu.Group context.
  // Radix allowed standalone labels outside groups. We use a plain div
  // to preserve the existing API while avoiding the context error.
  return (
    <div
      role="group"
      data-slot="menubar-label"
      data-inset={inset}
      className={cn('px-2 py-1.5 text-sm font-medium data-[inset]:pl-8', className)}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// MenubarSeparator
// ---------------------------------------------------------------------------

function MenubarSeparator({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      role="separator"
      data-slot="menubar-separator"
      className={cn('-mx-1 my-1 h-px bg-border', className)}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// MenubarShortcut
// ---------------------------------------------------------------------------

function MenubarShortcut({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="menubar-shortcut"
      className={cn('ml-auto text-xs tracking-widest text-muted-foreground', className)}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// MenubarSub (SubmenuRoot)
// ---------------------------------------------------------------------------

function MenubarSub({
  ...props
}: Omit<React.ComponentProps<typeof MenuPrimitive.SubmenuRoot>, 'children'> & {
  children?: React.ReactNode
}) {
  return <MenuPrimitive.SubmenuRoot data-slot="menubar-sub" {...props} />
}

// ---------------------------------------------------------------------------
// MenubarSubTrigger
// ---------------------------------------------------------------------------

function MenubarSubTrigger({
  className,
  inset,
  children,
  ...props
}: Omit<React.ComponentProps<typeof MenuPrimitive.SubmenuTrigger>, 'render'> & {
  inset?: boolean
  children?: React.ReactNode
}) {
  return (
    <MenuPrimitive.SubmenuTrigger
      data-slot="menubar-sub-trigger"
      data-inset={inset}
      className={cn(
        'flex cursor-default items-center rounded-sm px-2 py-2 text-sm outline-hidden select-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[inset]:pl-8 data-[popup-open]:bg-accent data-[popup-open]:text-accent-foreground',
        className,
      )}
      {...props}
    >
      {children}
      <ChevronRightIcon size="sm" className="ml-auto" />
    </MenuPrimitive.SubmenuTrigger>
  )
}

// ---------------------------------------------------------------------------
// MenubarSubContent
// ---------------------------------------------------------------------------
// Bakes in Portal > Positioner > Popup for the sub-menu.

function MenubarSubContent({
  className,
  children,
  ...props
}: {
  className?: string
  children?: React.ReactNode
} & Omit<React.ComponentProps<typeof MenuPrimitive.Popup>, 'className' | 'children'>) {
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Positioner>
        <MenuPrimitive.Popup
          data-slot="menubar-sub-content"
          className={cn(
            // DS: radius-lg (8 px) to match other floating menu surfaces.
            // Base UI: --transform-origin replaces --radix-menubar-content-transform-origin
            'z-50 min-w-[8rem] origin-[var(--transform-origin)] overflow-hidden rounded-lg border bg-popover p-1 text-popover-foreground shadow-lg outline-hidden transition-[opacity,scale] duration-150 scale-100 opacity-100 data-[starting-style]:scale-95 data-[starting-style]:opacity-0 data-[ending-style]:scale-95 data-[ending-style]:opacity-0',
            className,
          )}
          {...props}
        >
          {children}
        </MenuPrimitive.Popup>
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  )
}

export {
  Menubar,
  MenubarPortal,
  MenubarMenu,
  MenubarTrigger,
  MenubarContent,
  MenubarGroup,
  MenubarSeparator,
  MenubarLabel,
  MenubarItem,
  MenubarShortcut,
  MenubarCheckboxItem,
  MenubarRadioGroup,
  MenubarRadioItem,
  MenubarSub,
  MenubarSubTrigger,
  MenubarSubContent,
}
